import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import type { Campaign } from '@/lib/types';
import { OPPORTUNITY_DEFS, daysSince } from '@/lib/signals';
import { CHANNEL_LABEL } from '@/lib/messageTemplates';

interface CampaignDetailDrawerProps {
  campaign: Campaign | null;
  editing?: boolean;
  onClose: () => void;
  onPause: (campaign: Campaign) => void;
  onResume: (campaign: Campaign) => void;
  onDuplicate: (campaign: Campaign) => void;
  onDelete: (campaign: Campaign) => void;
}

const STATUS_LABEL: Record<Campaign['status'], string> = {
  active: 'Activa',
  paused: 'Pausada',
  completed: 'Completada',
  draft: 'Borrador',
};

const statusBadge: Record<Campaign['status'], string> = {
  active: 'bg-ok-tint text-ok-deep [&_.dot]:bg-ok',
  paused: 'bg-warn-tint text-warn-deep [&_.dot]:bg-warn',
  draft: 'bg-warn-tint text-warn-deep [&_.dot]:bg-warn',
  completed: 'bg-soft text-tertiary [&_.dot]:bg-muted',
};

function pct(num: number, denom: number): number {
  if (denom <= 0) return 0;
  return Math.round((num / denom) * 100);
}

function formatRelative(iso: string | undefined): string {
  const days = daysSince(iso);
  if (days === null) return '—';
  if (days === 0) return 'hoy';
  if (days === 1) return 'hace 1 día';
  if (days < 31) return `hace ${days} días`;
  if (days < 365) return `hace ${Math.round(days / 30)} meses`;
  return `hace ${(days / 365).toFixed(1)} años`;
}

export function CampaignDetailDrawer({
  campaign,
  editing,
  onClose,
  onPause,
  onResume,
  onDuplicate,
  onDelete,
}: CampaignDetailDrawerProps) {
  const open = campaign !== null;
  const def = campaign ? OPPORTUNITY_DEFS[campaign.kind] : null;
  const m = campaign?.metrics;
  const total = campaign?.recipientCount ?? 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="ai-drawer-fade-slow fixed inset-0 z-[100] bg-black/30 dark:bg-black/55 flex justify-end p-3.5 max-[760px]:p-2"
    >
      <DialogPanel className="ai-drawer-slide-slow relative w-full max-w-[480px] h-full bg-surface border border-line rounded-2xl flex flex-col overflow-hidden shadow-elevated">
        {campaign && def && m && (
          <>
            <header className="flex items-start justify-between gap-3 px-6 pt-5 pb-4 border-b border-line shrink-0">
              <div className="min-w-0 flex flex-col gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.02em] px-2 py-0.5 rounded-full ${statusBadge[campaign.status]}`}
                  >
                    <span className="dot w-[5px] h-[5px] rounded-full" />
                    {STATUS_LABEL[campaign.status]}
                  </span>
                  {editing && campaign.status === 'draft' && (
                    <span className="text-[10.5px] font-medium tracking-[0.04em] uppercase text-muted bg-soft px-1.5 py-0.5 rounded-sm">
                      Modo edición
                    </span>
                  )}
                </div>
                <DialogTitle className="text-[18px] font-semibold tracking-[-0.015em] m-0 text-primary leading-[1.25]">
                  {def.title}
                </DialogTitle>
                <p className="text-[12.5px] text-tertiary m-0 leading-[1.45]">
                  {def.suggestedAction}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="text-[13px] w-7 h-7 border border-line bg-transparent text-tertiary rounded-sm cursor-pointer transition-colors hover:bg-soft hover:text-primary shrink-0"
              >
                ✕
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-6 min-h-0">
              <section>
                <SectionHeader label="Resumen" />
                <dl className="grid grid-cols-2 gap-y-2.5 gap-x-4 text-[12.5px]">
                  <Row label="Canal" value={CHANNEL_LABEL[campaign.channel]} />
                  <Row label="Destinatarios" value={`${total}`} />
                  <Row label="Creada" value={formatRelative(campaign.createdAt)} />
                  {campaign.sentAt && (
                    <Row label="Enviada" value={formatRelative(campaign.sentAt)} />
                  )}
                  {campaign.completedAt && (
                    <Row label="Cerrada" value={formatRelative(campaign.completedAt)} />
                  )}
                </dl>
              </section>

              {campaign.status !== 'draft' && (
                <section>
                  <SectionHeader label="Métricas" />
                  <div className="grid grid-cols-2 gap-2">
                    <Metric label="Enviadas" value={m.delivered} total={total} />
                    <Metric label="Abrieron" value={m.opened} total={m.delivered} />
                    <Metric label="Respondieron" value={m.responded} total={m.delivered} />
                    <Metric label="Agendaron" value={m.scheduled} total={m.delivered} accent />
                  </div>
                </section>
              )}

              {campaign.status === 'draft' && (
                <section>
                  <SectionHeader label="Mensaje" />
                  <div className="bg-subtle border border-line rounded-md px-3.5 py-3 text-[12.5px] text-tertiary leading-[1.5]">
                    El mensaje se generará desde la plantilla de "{def.title}" antes de
                    enviar. Abre el editor para personalizar el tono y los chips de contexto.
                  </div>
                </section>
              )}
            </div>

            <footer className="px-6 py-4 border-t border-line bg-surface flex items-center gap-2 shrink-0 flex-wrap">
              {campaign.status === 'active' && (
                <PrimaryButton onClick={() => onPause(campaign)}>Pausar campaña</PrimaryButton>
              )}
              {campaign.status === 'paused' && (
                <PrimaryButton onClick={() => onResume(campaign)}>Reanudar campaña</PrimaryButton>
              )}
              <SecondaryButton onClick={() => onDuplicate(campaign)}>Duplicar</SecondaryButton>
              <span className="flex-1" />
              <DangerButton onClick={() => onDelete(campaign)}>Eliminar</DangerButton>
            </footer>
          </>
        )}
      </DialogPanel>
    </Dialog>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-muted mb-2.5">
      {label}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-tertiary">{label}</dt>
      <dd className="text-primary text-right tnum m-0">{value}</dd>
    </>
  );
}

interface MetricProps {
  label: string;
  value: number;
  total: number;
  accent?: boolean;
}

function Metric({ label, value, total, accent }: MetricProps) {
  const p = pct(value, total);
  return (
    <div
      className={`bg-subtle border rounded-md px-3 py-2.5 ${
        accent ? 'border-ok' : 'border-line'
      }`}
    >
      <div className="text-[10px] text-muted tracking-[0.04em] uppercase font-semibold mb-1">
        {label}
      </div>
      <div
        className={`text-xl font-bold tracking-[-0.02em] leading-[1.1] tnum ${
          accent && value > 0 ? 'text-ok-deep' : 'text-primary'
        }`}
      >
        {value}
        <span className="text-[11px] text-tertiary font-medium ml-1">
          /{total} · {p}%
        </span>
      </div>
    </div>
  );
}

function PrimaryButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[13px] font-medium text-surface bg-primary border-0 rounded-md px-3 py-2 cursor-pointer transition-opacity hover:opacity-90 active:translate-y-px"
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[13px] font-medium text-secondary bg-transparent border border-line rounded-md px-3 py-2 cursor-pointer transition-colors hover:bg-soft hover:border-line-default hover:text-primary"
    >
      {children}
    </button>
  );
}

function DangerButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[13px] font-medium text-crit-deep bg-transparent border border-line rounded-md px-3 py-2 cursor-pointer transition-colors hover:bg-crit-tint hover:border-crit"
    >
      {children}
    </button>
  );
}
